import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { 
  Target, Compass, Star, Flame, Trophy, Award, Crown, Gem,
  Lock, CheckCircle2
} from 'lucide-react';
import { useAuthStore } from '../lib/auth';
import api from '../lib/api';

const BADGE_ICONS = {
  target: Target,
  compass: Compass,
  star: Star,
  flame: Flame,
  trophy: Trophy,
  award: Award,
  crown: Crown,
  gem: Gem
};

const BADGE_COLORS = {
  blue: 'from-blue-500 to-blue-600',
  green: 'from-emerald-500 to-emerald-600',
  gold: 'from-yellow-400 to-amber-500',
  orange: 'from-orange-500 to-orange-600',
  purple: 'from-purple-500 to-purple-600',
  cyan: 'from-cyan-500 to-cyan-600',
  diamond: 'from-cyan-300 to-blue-400'
};

const RARITY_STYLES = {
  common: 'border-gray-500/30',
  uncommon: 'border-green-500/30',
  rare: 'border-blue-500/30',
  epic: 'border-purple-500/30',
  legendary: 'border-yellow-500/30 shadow-lg shadow-yellow-500/20'
};

export default function BadgesDisplay({ compact = false }) {
  const { user } = useAuthStore();
  const [badges, setBadges] = useState([]);
  const [stats, setStats] = useState({ earned: 0, total: 0, percentage: 0 });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (user?.id) {
      loadBadges();
    }
  }, [user?.id]);

  const loadBadges = async () => {
    try {
      const res = await api.get(`/api/v1/askalex/badges?user_id=${user.id}`);
      setBadges(res.data.badges || []);
      setStats({
        earned: res.data.earned_count,
        total: res.data.total_badges,
        percentage: res.data.completion_percentage
      });
    } catch (error) {
      console.error('Failed to load badges:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="animate-spin w-8 h-8 border-2 border-[hsl(var(--brand-gold-500))] border-t-transparent rounded-full"></div>
      </div>
    );
  }

  if (compact) {
    // Compact view for dashboard
    const earnedBadges = badges.filter(b => b.earned);
    return (
      <div className="flex flex-wrap gap-2">
        {earnedBadges.slice(0, 6).map(badge => {
          const IconComponent = BADGE_ICONS[badge.icon] || Trophy;
          const colorClass = BADGE_COLORS[badge.color] || BADGE_COLORS.gold;
          
          return (
            <div
              key={badge.badge_id}
              className={`w-10 h-10 rounded-full bg-gradient-to-br ${colorClass} flex items-center justify-center`}
              title={`${badge.name}: ${badge.description}`}
            >
              <IconComponent className="w-5 h-5 text-white" />
            </div>
          );
        })}
        {earnedBadges.length > 6 && (
          <div className="w-10 h-10 rounded-full bg-[hsl(var(--secondary))] flex items-center justify-center text-white text-sm font-medium">
            +{earnedBadges.length - 6}
          </div>
        )}
        {earnedBadges.length === 0 && (
          <p className="text-gray-400 text-sm">No badges earned yet</p>
        )}
      </div>
    );
  }

  // Full badges grid view
  return (
    <div>
      {/* Progress Header */}
      <div className="mb-6">
        <div className="flex items-center justify-between mb-2">
          <span className="text-white font-medium">Badge Progress</span>
          <span className="text-[hsl(var(--brand-gold-500))] font-bold">
            {stats.earned}/{stats.total}
          </span>
        </div>
        <Progress value={stats.percentage} className="h-2" />
        <p className="text-gray-400 text-sm mt-1">{stats.percentage}% Complete</p>
      </div>

      {/* Badges Grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {badges.map(badge => {
          const IconComponent = BADGE_ICONS[badge.icon] || Trophy;
          const colorClass = BADGE_COLORS[badge.color] || BADGE_COLORS.gold;
          const rarityClass = RARITY_STYLES[badge.rarity] || RARITY_STYLES.common;
          
          return (
            <Card 
              key={badge.badge_id}
              className={`bg-[hsl(var(--secondary))] border ${rarityClass} ${
                badge.earned ? '' : 'opacity-60'
              }`}
              data-testid={`badge-${badge.badge_id}`}
            >
              <CardContent className="p-4 text-center">
                {/* Badge Icon */}
                <div className={`w-16 h-16 mx-auto rounded-full flex items-center justify-center mb-3 ${
                  badge.earned 
                    ? `bg-gradient-to-br ${colorClass}` 
                    : 'bg-gray-700'
                }`}>
                  {badge.earned ? (
                    <IconComponent className="w-8 h-8 text-white" />
                  ) : (
                    <Lock className="w-6 h-6 text-gray-500" />
                  )}
                </div>
                
                {/* Badge Name */}
                <h4 className="text-white font-semibold text-sm mb-1">{badge.name}</h4>
                
                {/* Badge Description */}
                <p className="text-gray-400 text-xs mb-2 line-clamp-2">{badge.description}</p>
                
                {/* Earned Status or Progress */}
                {badge.earned ? (
                  <div className="flex items-center justify-center gap-1 text-emerald-400 text-xs">
                    <CheckCircle2 className="w-3 h-3" />
                    <span>Earned</span>
                  </div>
                ) : badge.progress ? (
                  <div>
                    <div className="flex items-center justify-between text-xs text-gray-400 mb-1">
                      <span>Progress</span>
                      <span>
                        {badge.progress.is_percentage 
                          ? `${badge.progress.current}%` 
                          : `${badge.progress.current}/${badge.progress.target}`
                        }
                      </span>
                    </div>
                    <Progress 
                      value={badge.progress.is_percentage 
                        ? (badge.progress.current / badge.progress.target) * 100
                        : (badge.progress.current / badge.progress.target) * 100
                      } 
                      className="h-1"
                    />
                  </div>
                ) : null}
                
                {/* XP Reward */}
                <Badge className="mt-2 bg-[hsl(var(--brand-gold-500))]/20 text-[hsl(var(--brand-gold-500))] border-[hsl(var(--brand-gold-500))]/30 text-xs">
                  +{badge.xp_reward} XP
                </Badge>
              </CardContent>
            </Card>
          );
        })}
      </div>
    </div>
  );
}
